import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TherapistContactProps } from '../../types';
import { useTherapistContactForm } from '../../hooks/useTherapistContactForm';

/**
 * Componente Contact renovado con diseño vanguardista y moderno
 */
const TherapistContact: React.FC<TherapistContactProps> = ({ therapist }) => {
  const navigate = useNavigate();
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Hook de contacto del terapeuta (envía vía EmailService → Resend)
  const {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    submitStatus,
    clearMessage
  } = useTherapistContactForm(therapist.name, therapist.id);

  useEffect(() => {
    // Configurar el Intersection Observer para animaciones scroll-triggered
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    // Observar todos los elementos animables
    const animatableElements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    animatableElements?.forEach(el => observerRef.current?.observe(el));

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  // Función para limpiar mensajes al hacer click en inputs
  const handleInputFocus = () => {
    if (submitStatus.type) {
      clearMessage();
    }
  };

  const handleWhatsAppClick = () => {
    setActiveChannel('whatsapp');
    setTimeout(() => setActiveChannel(null), 2000);
    
    const message = `Hola ${therapist.name.split(' ')[0]}, me gustaría agendar una consulta. 

Mi nombre es: [Tu nombre]
Motivo de consulta: [Describe brevemente]
Modalidad preferida: [Presencial/Online]

Quedo atento/a a tu respuesta. ¡Gracias!`;
    
    // Usar el número de WhatsApp específico del terapista si existe, sino usar el número general
    const whatsappNumber = therapist.socialMedia?.whatsapp || '+56978009119';
    const cleanNumber = whatsappNumber.replace(/\D/g, ''); // Eliminar caracteres no numéricos
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleChannelClick = (channel: string) => {
    setActiveChannel(channel);
    setTimeout(() => setActiveChannel(null), 1500);
  };

  const getScheduleStatus = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    if (currentDay === 0) { // Sunday
      if (currentHour >= 9 && currentHour < 14) {
        return { status: 'available', message: 'Disponible ahora' };
      } else {
        return { status: 'closed', message: 'Cerrado - Disponible domingos 9:00-14:00' };
      }
    } else if (currentDay === 6) { // Saturday
      if (currentHour >= 9 && currentHour < 17) {
        return { status: 'available', message: 'Disponible ahora' };
      } else {
        return { status: 'closed', message: 'Cerrado - Disponible sábados 9:00-17:00' };
      }
    } else { // Monday to Friday
      if (currentHour >= 9 && currentHour < 20) {
        return { status: 'available', message: 'Disponible ahora' };
      } else {
        return { status: 'closed', message: 'Cerrado - Disponible L-V 9:00-20:00' };
      }
    }
  };

  const scheduleStatus = getScheduleStatus();

  return (
    <section ref={sectionRef} id="contact" className="therapist-contact-modern">
      <div className="container">
        {/* Header moderno */}
        <div className="modern-section-header animate-on-scroll">
          <div className="header-decoration"></div>
          <h2 className="modern-section-title">
            Contacta con <span className="title-highlight">{therapist.name.split(' ')[0]}</span>
          </h2>
          <p className="modern-section-subtitle">
            Agenda tu cita o resuelve tus dudas de forma directa y personalizada
          </p>
          <div className="availability-status-modern">
            <div className={`status-indicator ${scheduleStatus.status}`}>
              <div className="status-pulse"></div>
              <i className={
                scheduleStatus.status === 'available' ? 'fas fa-circle' :
                scheduleStatus.status === 'emergency' ? 'fas fa-exclamation-triangle' :
                'fas fa-moon'
              }></i>
              <span>{scheduleStatus.message}</span>
            </div>
          </div>
        </div>

        <div className="contact-main-content-modern">
          {/* Información de horarios y modalidades modernos */}
          <div className="contact-info-modern animate-on-scroll">
            {/* Horarios de atención modernos */}
            <div className="schedule-card-modern">
              <div className="card-header-modern">
                <div className="header-icon-modern">
                  <i className="fas fa-clock"></i>
                  <div className="icon-glow"></div>
                </div>
                <h3 className="card-title-modern">Horarios de Atención</h3>
              </div>
              <div className="schedule-grid-modern">
                <div className="schedule-item-modern weekdays">
                  <div className="schedule-period">
                    <span className="period-label">Lunes - Viernes</span>
                    <div className="period-status active"></div>
                  </div>
                  <div className="schedule-time">
                    <span className="time-range">9:00 - 20:00</span>
                    <span className="time-duration">11 horas</span>
                  </div>
                </div>
                <div className="schedule-item-modern saturdays">
                  <div className="schedule-period">
                    <span className="period-label">Sábados</span>
                    <div className="period-status limited"></div>
                  </div>
                  <div className="schedule-time">
                    <span className="time-range">9:00 - 17:00</span>
                    <span className="time-duration">8 horas</span>
                  </div>
                </div>
                <div className="schedule-item-modern sundays">
                  <div className="schedule-period">
                    <span className="period-label">Domingos</span>
                    <div className="period-status limited"></div>
                  </div>
                  <div className="schedule-time">
                    <span className="time-range">9:00 - 14:00</span>
                    <span className="time-duration">5 horas</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modalidades modernas */}
            <div className="modalities-card-modern">
              <div className="card-header-modern">
                <div className="header-icon-modern">
                  <i className="fas fa-video"></i>
                  <div className="icon-glow"></div>
                </div>
                <h3 className="card-title-modern">Modalidades Disponibles</h3>
              </div>
              <div className="modalities-grid-modern">
                {therapist.availability.modalidades.map((modality, index) => (
                  <div 
                    key={index} 
                    className={`modality-card-modern ${modality}`}
                    style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}
                  >
                    <div className="modality-background">
                      <div className="modality-gradient"></div>
                    </div>
                    <div className="modality-icon-modern">
                      <i className={
                        modality === 'presencial' ? 'fas fa-building' :
                        modality === 'online' ? 'fas fa-laptop' :
                        'fas fa-sync-alt'
                      }></i>
                    </div>
                    <div className="modality-content">
                      <h4 className="modality-title">
                        {modality === 'presencial' ? 'Atención Presencial' :
                         modality === 'online' ? 'Consulta Online' :
                         'Modalidad Híbrida'}
                      </h4>
                      <p className="modality-description">
                        {modality === 'presencial' ? 'Bombero Adolfo Ossa 1010, Oficina 204' :
                         modality === 'online' ? 'Mediante plataforma de videollamadas' :
                         'Combina sesiones presenciales y online'}
                      </p>
                    </div>
                    <div className="modality-features">
                      {modality === 'presencial' ? (
                        <>
                          <div className="feature-item"><i className="fas fa-map-marker-alt"></i> Adolfo Ossa 1010, Of. 204</div>
                          <div className="feature-item"><i className="fas fa-handshake"></i> Atención directa</div>
                        </>
                      ) : modality === 'online' ? (
                        <>
                          <div className="feature-item"><i className="fas fa-video"></i> Plataforma de videollamadas</div>
                          <div className="feature-item"><i className="fas fa-shield-alt"></i> Privacidad total</div>
                        </>
                      ) : (
                        <>
                          <div className="feature-item"><i className="fas fa-sync-alt"></i> Flexibilidad</div>
                          <div className="feature-item"><i className="fas fa-star"></i> Lo mejor de ambos</div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Canales de contacto modernos */}
          <div className="contact-channels-modern animate-on-scroll">
            {/* WhatsApp moderno */}
            <div className={`contact-channel-modern whatsapp ${activeChannel === 'whatsapp' ? 'active' : ''}`}>
              <div className="channel-background">
                <div className="channel-gradient"></div>
                <div className="channel-pattern"></div>
              </div>
              <div className="channel-content">
                <div className="channel-header">
                  <div className="channel-icon-container">
                    <i className="fab fa-whatsapp"></i>
                    <div className="icon-pulse"></div>
                  </div>
                  <div className="channel-info">
                    <h3>Contacto Inmediato</h3>
                    <p>¿Necesitas respuesta rápida? Escríbele directamente por WhatsApp.</p>
                  </div>
                </div>
                <button 
                  className="btn-channel-modern whatsapp-btn-enhanced"
                  onClick={handleWhatsAppClick}
                >
                  <div className="btn-content">
                    <i className="fab fa-whatsapp"></i>
                    <span>Escribir por WhatsApp</span>
                  </div>
                  <div className="btn-meta">
                    <small>Respuesta en minutos</small>
                  </div>
                  <div className="btn-glow"></div>
                </button>
              </div>
            </div>

            {/* Formulario moderno */}
            <div className="contact-form-modern">
              <div className="form-background">
                <div className="form-gradient"></div>
                <div className="form-pattern"></div>
              </div>
              <div className="form-header-modern">
                <div className="form-icon-container">
                  <i className="fas fa-envelope"></i>
                  <div className="icon-pulse"></div>
                </div>
                <div className="form-info">
                  <h3>Formulario de Contacto</h3>
                  <p>Déjanos tus datos y {therapist.name.split(' ')[0]} se contactará contigo.</p>
                </div>
              </div>
              
              <form className="modern-contact-form" onSubmit={handleSubmit}>
                <div className="form-row-modern">
                  <div className={`form-group-modern ${errors.name ? 'error' : ''}`}>
                    <div className="input-container">
                      <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                        id="name"
                        className="modern-input"
                        placeholder=" "
                        onFocus={handleInputFocus}
                        {...register('name', {
                          required: 'El nombre es requerido',
                          minLength: {
                            value: 2,
                            message: 'El nombre debe tener al menos 2 caracteres'
                          },
                          maxLength: {
                            value: 50,
                            message: 'El nombre no puede exceder 50 caracteres'
                          },
                          pattern: {
                            value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/,
                            message: 'El nombre solo puede contener letras y espacios'
                          },
                          validate: {
                            notOnlySpaces: (value) => value.trim().length > 0 || 'El nombre no puede ser solo espacios',
                            validWords: (value) => {
                              const words = value.trim().split(/\s+/);
                              return words.length >= 2 || 'Por favor ingresa nombre y apellido';
                            }
                          }
                        })}
                      />
                      <div className="input-border"></div>
                      <label htmlFor="name" className="floating-label">Nombre Completo *</label>
                    </div>
                    {errors.name && <span className="error-message">{errors.name.message}</span>}
                  </div>
                  <div className={`form-group-modern ${errors.phone ? 'error' : ''}`}>
                    <div className="input-container">
                      <i className="fas fa-phone input-icon"></i>
                      <input
                        type="tel"
                        id="phone"
                        className="modern-input"
                        placeholder=" "
                        onFocus={handleInputFocus}
                        {...register('phone', {
                          required: 'El teléfono es requerido',
                          validate: {
                            validChileanPhone: (value) => {
                              const cleaned = value.replace(/[\s\-()]/g, '');
                              const withoutCountryCode = cleaned.replace(/^\+?56/, '');
                              
                              // Verificar que sea un número chileno válido
                              if (!/^[2-9]\d{7,8}$/.test(withoutCountryCode)) {
                                return 'Ingresa un número chileno válido (ej: 912345678)';
                              }
                              
                              // Verificar longitud específica
                              if (withoutCountryCode.length < 8 || withoutCountryCode.length > 9) {
                                return 'El número debe tener 8 o 9 dígitos';
                              }
                              
                              return true;
                            }
                          }
                        })}
                      />
                      <div className="input-border"></div>
                      <label htmlFor="phone" className="floating-label">Teléfono *</label>
                    </div>
                    {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                  </div>
                </div>

                <div className={`form-group-modern ${errors.email ? 'error' : ''}`}>
                  <div className="input-container">
                    <i className="fas fa-envelope input-icon"></i>
                    <input
                      type="email"
                      id="email"
                      className="modern-input"
                      placeholder=" "
                      onFocus={handleInputFocus}
                      {...register('email', {
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: 'Ingresa un correo electrónico válido'
                        },
                        maxLength: {
                          value: 100,
                          message: 'El correo no puede exceder 100 caracteres'
                        },
                        validate: {
                          validDomain: (value) => {
                            if (!value) return true; // Email es opcional
                            const domainPart = value.split('@')[1];
                            if (!domainPart) return 'Formato de correo inválido';
                            return domainPart.includes('.') || 'Dominio de correo inválido';
                          },
                          noConsecutiveDots: (value) => {
                            if (!value) return true;
                            return !value.includes('..') || 'El correo no puede tener puntos consecutivos';
                          },
                          validStart: (value) => {
                            if (!value) return true;
                            return !/^[.-]/.test(value) || 'El correo no puede comenzar con punto o guión';
                          }
                        }
                      })}
                    />
                    <div className="input-border"></div>
                    <label htmlFor="email" className="floating-label">Email</label>
                  </div>
                  {errors.email && <span className="error-message">{errors.email.message}</span>}
                </div>

                <div className={`form-group-modern textarea ${errors.reason ? 'error' : ''}`}>
                  <div className="input-container">
                    <i className="fas fa-comment-dots input-icon"></i>
                    <textarea
                      id="reason"
                      rows={4}
                      className="modern-textarea"
                      placeholder=" "
                      onFocus={handleInputFocus}
                      {...register('reason', {
                        required: 'El motivo de consulta es requerido',
                        minLength: {
                          value: 10,
                          message: 'Describe brevemente el motivo de tu consulta (mínimo 10 caracteres)'
                        },
                        maxLength: {
                          value: 500,
                          message: 'El motivo de consulta no puede exceder 500 caracteres'
                        },
                        validate: {
                          notOnlySpaces: (value) => value.trim().length >= 10 || 'El motivo debe tener al menos 10 caracteres válidos',
                          hasValidContent: (value) => {
                            const cleanText = value.trim().replace(/\s+/g, ' ');
                            const words = cleanText.split(' ').filter(word => word.length > 0);
                            return words.length >= 3 || 'Por favor describe tu consulta con al menos 3 palabras';
                          }
                        }
                      })}
                    />
                    <div className="input-border"></div>
                    <label htmlFor="reason" className="floating-label">Motivo de Consulta *</label>
                  </div>
                  {errors.reason && <span className="error-message">{errors.reason.message}</span>}
                </div>

                <div className="form-actions-modern">
                  <button 
                    type="submit" 
                    className={`btn-submit-modern ${isSubmitting ? 'submitting' : ''} ${submitStatus.type === 'success' ? 'success' : ''}`}
                    disabled={isSubmitting}
                  >
                    <div className="btn-content">
                      {isSubmitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Enviando...</span>
                        </>
                      ) : submitStatus.type === 'success' ? (
                        <>
                          <i className="fas fa-check"></i>
                          <span>¡Enviado!</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          <span>Enviar Mensaje</span>
                        </>
                      )}
                    </div>
                    <div className="btn-progress">
                      <div className="progress-bar"></div>
                    </div>
                    <div className="btn-glow"></div>
                  </button>
                </div>

                {submitStatus.type === 'success' && (
                  <div className="form-message-modern success">
                    <div className="message-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                    <div className="message-content">
                      <h4>¡Mensaje enviado exitosamente!</h4>
                      <p>{submitStatus.message}</p>
                    </div>
                  </div>
                )}

                {submitStatus.type === 'error' && (
                  <div className="form-message-modern error">
                    <div className="message-icon">
                      <i className="fas fa-exclamation-circle"></i>
                    </div>
                    <div className="message-content">
                      <h4>Error al enviar el mensaje</h4>
                      <p>{submitStatus.message}</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Garantías modernas - Movidas después del formulario */}
        <div className="guarantees-section-modern animate-on-scroll">
          <div className="guarantees-card-modern">
            <div className="card-header-modern">
              <div className="header-icon-modern">
                <i className="fas fa-award"></i>
                <div className="icon-glow"></div>
              </div>
              <h3 className="card-title-modern">Garantías y Beneficios</h3>
            </div>
            <div className="guarantees-grid-modern">
              <div className="guarantee-item-modern">
                <div className="guarantee-icon">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className="guarantee-content">
                  <h4>Confidencialidad garantizada</h4>
                  <p>Código de ética profesional estricto</p>
                </div>
              </div>
              <div className="guarantee-item-modern">
                <div className="guarantee-icon">
                  <i className="fas fa-file-invoice"></i>
                </div>
                <div className="guarantee-content">
                  <h4>Boletas para reembolso Isapre</h4>
                  <p>Documentos válidos para seguros</p>
                </div>
              </div>
              <div className="guarantee-item-modern">
                <div className="guarantee-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="guarantee-content">
                  <h4>Sesiones de {therapist.availability.sessionDuration}</h4>
                  <p>Duración adaptada a tus necesidades</p>
                </div>
              </div>
              {therapist.availability.emergencyContact && (
                <div className="guarantee-item-modern emergency">
                  <div className="guarantee-icon">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                  <div className="guarantee-content">
                    <h4>Contacto de emergencia disponible</h4>
                    <p>Apoyo inmediato en crisis</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendario moderno */}
        <div className="calendar-section-modern animate-on-scroll">
          <div className="calendar-header-modern">
            <div className="header-icon-modern">
              <i className="fas fa-calendar-plus"></i>
              <div className="icon-glow"></div>
            </div>
            <div className="header-content">
              <h3 className="calendar-title-modern">Agenda tu Cita</h3>
              <p className="calendar-subtitle-modern">Selecciona el día y hora que mejor te acomode para tu consulta.</p>
            </div>
          </div>
          
          <div className="calendar-placeholder-modern">
            <div className="calendar-background">
              <div className="calendar-gradient"></div>
              <div className="calendar-pattern"></div>
            </div>
            <div className="calendar-content-modern">
              <div className="calendar-icon-modern">
                <i className="fas fa-calendar-alt"></i>
                <div className="calendar-pulse"></div>
              </div>
              <div className="calendar-text">
                <h4>Agenda tu Cita</h4>
                <p>Sistema de agendamiento online — rápido, seguro y sin llamadas.</p>
                <div className="calendar-features">
                  <div className="feature"><i className="fas fa-clock"></i> Horarios en tiempo real</div>
                  <div className="feature"><i className="fas fa-bell"></i> Recordatorios automáticos</div>
                  <div className="feature"><i className="fas fa-sync"></i> Reagendar fácilmente</div>
                </div>
              </div>
            </div>
            <div className="calendar-cta">
              <p>Agenda tu cita de manera fácil y segura con nuestro sistema online.</p>
              <button
                className="btn-modern btn-primary-calendar"
                onClick={() => navigate(`/agendar?therapistId=${therapist.id}`)}
              >
                <i className="fas fa-calendar-plus"></i>
                <span>Agendar Cita Online</span>
              </button>
              <button 
                className="btn-modern btn-outline"
                onClick={handleWhatsAppClick}
              >
                <i className="fab fa-whatsapp"></i>
                <span>Coordinar por WhatsApp</span>
              </button>
            </div>
          </div>
        </div>

        {/* Redes sociales modernas */}
        {therapist.socialMedia && (therapist.socialMedia.instagram || therapist.socialMedia.linkedin || therapist.socialMedia.tiktok || therapist.socialMedia.facebook || therapist.socialMedia.twitter || therapist.socialMedia.youtube) && (
          <div className="social-section-modern animate-on-scroll">
            <div className="social-header-modern">
              <div className="header-icon-modern">
                <i className="fas fa-share-alt"></i>
                <div className="icon-glow"></div>
              </div>
              <div className="header-content">
                <h3 className="social-title-modern">Sígueme en Redes Sociales</h3>
                <p className="social-subtitle-modern">Contenido educativo y tips de bienestar emocional</p>
              </div>
            </div>
            
            <div className="social-grid-compact">
              {therapist.socialMedia.instagram && (
                <a 
                  href={therapist.socialMedia.instagram}
                  className="social-card-compact instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleChannelClick('instagram')}
                >
                  <div className="social-compact-background">
                    <div className="social-compact-gradient"></div>
                  </div>
                  <div className="social-compact-content">
                    <div className="social-compact-icon">
                      <i className="fab fa-instagram"></i>
                    </div>
                    <div className="social-compact-info">
                      <h5>Instagram</h5>
                      <span>Tips y reflexiones</span>
                    </div>
                  </div>
                  <div className="social-compact-arrow">
                    <i className="fas fa-arrow-right"></i>
                  </div>
                </a>
              )}
              {therapist.socialMedia.facebook && (
                <a 
                  href={therapist.socialMedia.facebook}
                  className="social-card-compact facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleChannelClick('facebook')}
                >
                  <div className="social-compact-background">
                    <div className="social-compact-gradient"></div>
                  </div>
                  <div className="social-compact-content">
                    <div className="social-compact-icon">
                      <i className="fab fa-facebook"></i>
                    </div>
                    <div className="social-compact-info">
                      <h5>Facebook</h5>
                      <span>Comunidad y apoyo</span>
                    </div>
                  </div>
                  <div className="social-compact-arrow">
                    <i className="fas fa-arrow-right"></i>
                  </div>
                </a>
              )}
              {therapist.socialMedia.tiktok && (
                <a 
                  href={therapist.socialMedia.tiktok}
                  className="social-card-compact tiktok"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleChannelClick('tiktok')}
                >
                  <div className="social-compact-background">
                    <div className="social-compact-gradient"></div>
                  </div>
                  <div className="social-compact-content">
                    <div className="social-compact-icon">
                      <i className="fab fa-tiktok"></i>
                    </div>
                    <div className="social-compact-info">
                      <h5>TikTok</h5>
                      <span>Videos educativos</span>
                    </div>
                  </div>
                  <div className="social-compact-arrow">
                    <i className="fas fa-arrow-right"></i>
                  </div>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TherapistContact;